import React, { memo, useCallback } from 'react';
import { MenuItem, Select, FormControl, InputLabel, SelectChangeEvent } from '@mui/material';

export interface ModelOption {
  id: string;
  name: string;
  provider: string;
  mediaTypes: string[];
  maxConnections?: number;
}

export interface ModelSelectorProps {
  label: string;
  value: string;
  onChange: (modelId: string) => void;
  models: ModelOption[];
  disabled?: boolean;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple';
}

const ModelSelectorComponent: React.FC<ModelSelectorProps> = ({
  label,
  value,
  onChange,
  models,
  disabled = false,
  variant = 'neutral',
}) => {
  // Memoize the change handler
  const handleChange = useCallback((e: SelectChangeEvent<string>) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <FormControl 
      fullWidth 
      size="small" 
      disabled={disabled}
      className={`nodrag model-selector-container model-selector-${variant}`}
      sx={{ marginBottom: '4px' }}
    >
      <InputLabel sx={{ 
        fontSize: '12px', 
        color: '#F3F4F6', 
        transform: 'translate(8px, 6px) scale(1)', 
        '&.MuiInputLabel-shrink': { transform: 'translate(12px, -6px) scale(0.75)' },
        '&.Mui-disabled': { color: 'var(--color-disabled-text)' }
      }}>{label}</InputLabel>
      <Select
        value={value}
        onChange={handleChange}
        label={label}
        className="model-selector"
        sx={{ 
          fontSize: '12px',
          color: '#F3F4F6',
          '& .MuiSelect-select': { padding: '4px 8px', minHeight: 'auto', color: '#F3F4F6' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#27272A' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3F3F46' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3F3F46' },
          '&.Mui-disabled': {
            color: 'var(--color-disabled-text)',
            backgroundColor: 'var(--color-disabled-bg)',
            '& .MuiSelect-select': { color: 'var(--color-disabled-text)' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-disabled-border)' },
            '& .MuiSelect-icon': { color: 'var(--color-disabled-text-muted)' }
          }
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 200,
              backgroundColor: '#18181B',
              border: '1px solid #52525B',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
              zIndex: 9999,
              '& .MuiMenuItem-root': {
                fontSize: '12px',
                minHeight: '28px',
                padding: '4px 12px',
                lineHeight: 1.2,
                color: '#F3F4F6',
                backgroundColor: '#18181B',
                '&:hover': {
                  backgroundColor: '#27272A'
                },
                '&.Mui-selected': {
                  backgroundColor: '#27272A',
                  '&:hover': {
                    backgroundColor: '#3F3F46'
                  }
                }
              }
            }
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          }
        }}
      >
        {models.filter(model => model != null).map((model) => (
          <MenuItem key={model.id} value={model.id}>
            {model.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * Memoized ModelSelector to prevent unnecessary re-renders during node dragging
 */
export const ModelSelector = memo(ModelSelectorComponent, (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.label === nextProps.label &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.variant === nextProps.variant &&
    prevProps.models === nextProps.models &&
    prevProps.onChange === nextProps.onChange
  );
});
